
import _ from "lodash";
import { VirkeskopskontraktPrisBerakningsTyper, VirkeskopskontraktPrisTyper } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ISearchHandelssortimentModel } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { SortimentDTO, VirkeskopskontraktPrisDTO } from "../../dto/pristabell/pristabell-dtos";
import { SortimentSnittnettoRad } from "../vardeberakning/vardeberakning-models";

export type RowHeaderAction = "copy" | "remove";
type RowHeaderOption = {
    text: string;
    action: RowHeaderAction;
};

interface Sortiment {
    id: number;
    kod: string;
}

export class PristabellRad {
    id?: number;
    handelssortiment_Id?: number;
    handelssortimentsgrupp_Id?: number;
    marknadsprislistaViol3: boolean;
    marknadsprislistaId?: number;
    anteckning: string;
    mattslag: string;
    tillaggAvdrag?: number;
    sortimentsnetto?: number;
    fsc?: number;
    pefc?: number;
    visasIUtskrift?: boolean;
    autokompletterad: boolean;
    extraMattslagsrad: boolean;
    prisberakning: string;
    handelssortimentNamn: string;
    handelssortimentsgruppNamn: string;
    handelssortimentsgruppSekvensnummer: number;
    typ: string;
    kopieradRad: boolean;
    inmatningshandelssortiment: string;
    sortimentId?: number;
    marknadsprislistaVidLeveransGallande?: boolean;

    private readonly _sortimentDto?: SortimentDTO;
    private _handelssortimentList: Sortiment[];

    get hasHeaderOptions() {
        return this.typ === VirkeskopskontraktPrisTyper.Handelssortiment
            && !this.autokompletterad
            && !this.extraMattslagsrad;
    }

    get hasPrislista() {
        return this.prisberakning === VirkeskopskontraktPrisBerakningsTyper.Prislista
            && !this.autokompletterad;
    }

    get isSortimentsnetto() {
        return this.prisberakning === VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto
            && !this.autokompletterad;
    }

    get hasTillaggAvdrag() {
        return !this.autokompletterad;
    }

    get sortimentListaDisabled() {
        return this.isHsGrupp
            || this.autokompletterad
            || this.extraMattslagsrad;
    }

    get sortimentLista() {
        return this._handelssortimentList;
    }

    get isHsGrupp() {
        return this.typ === VirkeskopskontraktPrisTyper.Handelssortimentgrupp;
    }

    get headerOptions(): RowHeaderOption[] {
        const options: RowHeaderOption[] = [];
        if (this.kopieradRad) {
            options.push({
                action: "remove",
                text: "Ta bort rad"
            });
        } else {
            options.push({
                action: "copy",
                text: "Kopiera rad"
            });
        }
        return options;
    }

    get uniqueKey(): string {
        return `${this.handelssortiment_Id ?? "null"}-${this.handelssortimentsgrupp_Id ?? "null"}-${this.kopieradRad}-${this.inmatningshandelssortiment}`;
    }

    constructor(dto?: VirkeskopskontraktPrisDTO) {
        this.id = dto?.id;
        this.handelssortiment_Id = dto?.handelssortiment_Id;
        this.handelssortimentsgrupp_Id = dto?.handelssortimentsgrupp_Id;
        this.marknadsprislistaViol3 = dto?.marknadsprislistaViol3 ?? false;
        this.marknadsprislistaId = dto?.marknadsprislistaId;
        this.anteckning = dto?.anteckning ?? "";
        this.mattslag = dto?.mattslag ?? "";
        this.tillaggAvdrag = dto?.tillaggAvdrag;
        this.sortimentsnetto = dto?.sortimentsnetto;
        this.fsc = dto?.fsc;
        this.pefc = dto?.pefc;
        this.visasIUtskrift = dto?.visasIUtskrift;
        this.autokompletterad = dto?.autokompletterad ?? false;
        this.extraMattslagsrad = dto?.extraMattslagsrad ?? false;
        this.prisberakning = dto?.prisberakning ?? VirkeskopskontraktPrisBerakningsTyper.Prislista;

        if (dto?.typ === VirkeskopskontraktPrisTyper.Snittnetto) {
            this.handelssortimentNamn = "Samtliga/övriga";
            this.handelssortimentsgruppNamn = "";
        } else {
            this.handelssortimentNamn = dto?.handelssortimentNamn;
            this.handelssortimentsgruppNamn = dto?.handelssortimentsgruppNamn;
        }

        this.handelssortimentsgruppSekvensnummer = dto?.handelssortimentsgruppSekvensnummer;
        this.typ = dto?.typ;
        this.kopieradRad = dto?.kopieradRad;
        this.inmatningshandelssortiment = dto?.inmatningshandelssortiment;
        this.marknadsprislistaVidLeveransGallande = dto?.marknadsprislistaVidLeveransGallande;
        this._sortimentDto = dto?.sortiment;
        this.sortimentId = dto?.sortiment?.id;
    }

    initHandelssortiment(hs?: ISearchHandelssortimentModel) {
        const grupp = hs?.handelssortimentsgrupp;
        if (this.typ !== VirkeskopskontraktPrisTyper.Snittnetto
            && grupp
            && !this.handelssortimentsgrupp_Id
        ) {
            this.handelssortimentsgrupp_Id = grupp.id;
            this.handelssortimentsgruppNamn = grupp.namn;
            this.handelssortimentsgruppSekvensnummer = grupp.sekvensnummer;
        }

        this._handelssortimentList = (hs?.sortiment ?? [])
            .filter(x => x.kod.length === 4)
            .map(({ id, kod }) => ({
                id,
                kod
            }));

        // Prisradens sortiment kan saknas bland handelssortimentets sortiment och läggs i så fall till
        if (this._sortimentDto && !this._handelssortimentList.some(x => x.id === this._sortimentDto.id)) {
            this._handelssortimentList.unshift({ ...this._sortimentDto });
        }
    }

    initSortimentsnetto(snittnettoRad?: SortimentSnittnettoRad, useApris = false) {

        if (snittnettoRad) {
            this.sortimentsnetto = useApris ? Math.round(snittnettoRad.apris) : Math.round(snittnettoRad.kopeskilling);
        }

        if (this.prisberakning === VirkeskopskontraktPrisBerakningsTyper.Sortimentsnetto) {
            this.tillaggAvdrag = null;
            this.marknadsprislistaId = null;
        }
    }

    toDto(): VirkeskopskontraktPrisDTO {
        const {
            sortimentId, _sortimentDto, _handelssortimentList, ...rest
        } = this;
        let sortiment: Sortiment;
        if (sortimentId) {
            sortiment = this._handelssortimentList?.find(x => x.id === sortimentId);
            if (!sortiment) {
                sortiment = { id: sortimentId,
                    kod: "" };
            }
        }

        return {
            ...rest,
            sortiment: sortiment ? { ...sortiment } : undefined
        };
    }

    getSortiment(sortimentId: number): SortimentDTO {
        const sortiment = this._handelssortimentList?.find(x => x.id === sortimentId);
        if (!ObjectUtils.hasValue(sortiment) && sortimentId) {
            return {
                id: sortimentId,
                kod: ""
            };
        }
        return {
            ...sortiment
        };
    }

    copy(): PristabellRad {
        const copy = new PristabellRad();
        copy.kopieradRad = true;

        // kopiera inte id
        const {
            id, kopieradRad, ...rest
        } = this;
        _.merge(copy, rest);
        return copy;
    }
}
